import React from "react";
import { toast } from "sonner";
import { getSafeToastMessageFromError, logApiFailure } from "@/main/common/ApiUtils";
import type { Localizer } from "@/main/react/Types";
import type { LocalizationKey } from "@/main/util/LocalizationUtil";
import { REJECT_UNDO_TOAST_MS } from "@/main/react/Dialog/submissionRejectedToast";

/**
 * SV-146 (D7): the "…ignored. They won't be notified." toast with its Undo, shared by all three leader
 * queues (join requests, invite requests, post submissions) and every surface that offers Ignore on them.
 *
 * <p>Lifetime is the reject undo's `REJECT_UNDO_TOAST_MS`, for the same reason: the server accepts an
 * undo for `IGNORE_UNDO_WINDOW` (15 s from `ignored_at`, the same leader only), so the Undo must end
 * before the server would refuse it. sonner pauses a toast's timer on hover, so it is also dismissed on a
 * hard timer.
 *
 * <p>`undo` is the queue's own `undoIgnore*` call; `onUndone` puts the row back where the leader was
 * looking. A refused undo (window closed, a demoted leader) reuses the generic error copy, and the
 * server's own message wins when it is a safe one.
 */
export function showIgnoredUndoToast({
    localizer,
    message,
    undo,
    onUndone,
}: {
    localizer: Localizer;
    message: LocalizationKey;
    undo: () => Promise<unknown>;
    onUndone?: () => void;
}) {
    const runUndo = async () => {
        try {
            await undo();
            onUndone?.();
        } catch (error: unknown) {
            logApiFailure("Failed undoing a group queue ignore", error);
            toast.error(getSafeToastMessageFromError(error, localizer("typescript.discussion.server_error_generic")));
        }
    };

    const toastId = toast.success(localizer(message), {
        duration: REJECT_UNDO_TOAST_MS,
        action: {
            // The stable hook rides inside the label (the reject-undo precedent), so a spec can press it
            // without depending on the localized word.
            label: <span data-playwright-id="group-queue-ignore-undo-action">{localizer("literal.undo")}</span>,
            onClick: () => runUndo(),
        },
    });
    window.setTimeout(() => toast.dismiss(toastId), REJECT_UNDO_TOAST_MS);
}
